import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableVirtuoso, TableComponents } from 'react-virtuoso';
import { colors } from '../../../theme';
import { CALCULATION_MODE } from '../../Modals/DashboardModal/constants';
import { CrosstableProps, Evolution } from '../dashboardInterfaces';
import { EVOLUTION_ICONS, getCrosstableEvolution } from '../IndicatorCard/evolution';
import { TableCellBodyStyled, TableCellHeadStyled, TableCellBaseStyled } from '../styled';

const EVOLUTION_ICON_SIZE = 12;
// Lines a row label may take in the first column before it is clipped.
const LABEL_MAX_LINES = 2;
// Height of the horizontal scrollbar, kept below the rows so it never hides the last one.
const HORIZONTAL_SCROLLBAR_HEIGHT = 8;
// First guess of the height, before the table has measured its own rows.
const ESTIMATED_HEADER_HEIGHT = 130;
const ESTIMATED_ROW_HEIGHT = 42;

interface CrosstableIndicatorProps {
    crosstableResult: CrosstableProps
}

interface ColumnData {
    dataKey: number;
    label: string;
    numeric?: boolean;
    width?: number;
}

interface RowData {
    res?: number;
    type?: string;
    evolution?: Evolution | null;
}

const scrollbarStyles = {
    '&::-webkit-scrollbar': {
        height: `${HORIZONTAL_SCROLLBAR_HEIGHT}px`,
        width: '12px',
    },
    '&::-webkit-scrollbar-track': {
        background: colors.grey600,
        borderRadius: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: colors.grey400,
        borderRadius: '6px',
        '&:hover': {
            background: '#555',
        },
    },
};

const VirtuosoTableComponents: TableComponents<any> = {
    Scroller: React.forwardRef<HTMLDivElement>((props, ref) => (
        <TableContainer component={Paper} {...props} ref={ref} sx={{ ...scrollbarStyles }} />
    )),
    Table: (props) => (
        <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />
    ),
    TableHead: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
        <TableHead {...props} ref={ref} />
    )),
    TableRow,
    TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
        <TableBody {...props} ref={ref} />
    )),
};

const formateValue = ({ res, type }: RowData) => {
    if (!res) {
        return '-';
    }

    switch (type) {
        case CALCULATION_MODE.PERCENTAGE: return `${res}%`;
        case CALCULATION_MODE.AVERAGE: return res;
        case CALCULATION_MODE.NPS: return `${res >= 0 ? '+' : ''}${res}`;
        default: return res;
    }
};

// The value, followed by its icon when the column differs significantly from the total.
const renderCellContent = (rowData: RowData) => {
    const EvolutionIcon = rowData?.evolution ? EVOLUTION_ICONS[rowData.evolution] : null;
    if (!EvolutionIcon) {
        return formateValue(rowData ?? {});
    }

    return (
        // The files carry their fill on each path, it follows the text color of the cell there.
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', '& path': { fill: 'currentColor' } }}>
            {formateValue(rowData)}
            <EvolutionIcon height={EVOLUTION_ICON_SIZE} width={EVOLUTION_ICON_SIZE} />
        </Box>
    );
};

const getLabelForSpecificRow = (rowId: number, t: any) => {
    if (!rowId) {
        return 'Error';
    }

    switch (rowId) {
        case -9998: return t('addIndicatorModal.selectDisplayType.indicatorChart.crosstableMean');
        case -9999: return t('addIndicatorModal.selectDisplayType.indicatorChart.crosstableNPS');
        default: return 'Error';
    }
};

const getLabelColumnWidth = (labels: string[]) => {
    const maxLength = Math.max(...labels.map((label) => label.length));
    const columnWidth = maxLength * 7;

    return (columnWidth > 180) ? 180 : columnWidth;
};

const CrosstableIndicator: FC<CrosstableIndicatorProps> = ({ crosstableResult }) => {
    // The table takes the height of its content, header included, so it never scrolls vertically.
    const [contentHeight, setContentHeight] = useState<number | null>(null);
    if (crosstableResult.base === 0) {
        return null;
    }
    const { t } = useTranslation('dashboard');
    const labelColumnWidth = getLabelColumnWidth(crosstableResult.rows.map((row) => row.label));
    const totalColumnPosition = labelColumnWidth + 32;
    const columns: ColumnData[] = crosstableResult.columns.map((column, index) => ({ width: index === 0 ? 50 : 100, label: column.label, dataKey: column.id, computeMethod: crosstableResult.computeMethod }));
    const dataRows: RowData[][] = crosstableResult.rows.map((row) => (
        crosstableResult.datasets.map((dataset) => {
            // Find matching data for the current row and dataset
            const matchingData = dataset.datas.find((data) => data.modal === row.id);

            return {
                res: matchingData?.data.current?.res,
                type: matchingData?.type,
                evolution: getCrosstableEvolution(matchingData?.significance),
            };
        })
    ));

    const fixedHeaderContent = () => (
        <>
            <TableRow>
                <TableCell
                    style={{ width: labelColumnWidth }}
                    sx={{ backgroundColor: 'background.paper', border: 'none', position: 'sticky', left: 0, zIndex: 10 }}
                    variant="head"
                />
                {columns.map((column, index) => (
                    <TableCellHeadStyled
                        key={column.dataKey}
                        $cellWidth={column.width}
                        $isTotal={index === 0}
                        align='center'
                        sx={index === 0 ? { backgroundColor: 'background.paper', border: 'none', position: 'sticky', left: totalColumnPosition, zIndex: 10 } : { backgroundColor: 'background.paper', border: 'none', position: 'sticky', left: 0 }}
                        variant='head'
                    >
                        {column.label}
                    </TableCellHeadStyled>
                ))}
            </TableRow>
            <TableRow>
                <TableCellBaseStyled
                    align='right'
                    style={{ width: labelColumnWidth, padding: 8 }}
                    sx={{ backgroundColor: 'background.paper', position: 'sticky', left: 0, zIndex: 10, color: colors.grey100, fontStyle: 'italic' }}
                    variant="head"
                    $isFirstColumn
                >
                    Bases :
                </TableCellBaseStyled>
                {columns.map((column, index) => (
                    <TableCellBaseStyled
                        key={column.dataKey}
                        $cellWidth={column.width}
                        $isTotal={index === 0}
                        align='center'
                        sx={index === 0 ? { backgroundColor: 'background.paper', position: 'sticky', left: totalColumnPosition, zIndex: 10 } : { backgroundColor: 'background.paper', border: 'none', position: 'sticky', left: 0 }}
                        variant='head'
                    >
                        {crosstableResult.bases[index]}
                    </TableCellBaseStyled>
                ))}
            </TableRow>
        </>
    );

    const getRowLabel = (rowIndex: number) => (
        crosstableResult.rows[rowIndex].id <= -9998 ? getLabelForSpecificRow(crosstableResult.rows[rowIndex].id, t) : crosstableResult.rows[rowIndex].label
    );

    const rowContent = (rowIndex: number, row: any) => (
        <>
            <TableCellHeadStyled $cellWidth={labelColumnWidth} align='right' sx={{ background: 'white', position: 'sticky', left: 0, border: 'none' }}>
                {/* Two lines at most, the whole label stays readable on hover. */}
                <Box
                    sx={{
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: LABEL_MAX_LINES,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        overflowWrap: 'anywhere',
                    }}
                    title={getRowLabel(rowIndex)}
                >
                    {getRowLabel(rowIndex)}
                </Box>
            </TableCellHeadStyled>
            {columns.map((column, index) => (
                <TableCellBodyStyled
                    key={column.dataKey}
                    $cellWidth={column.width}
                    $isEven={rowIndex % 2 === 0}
                    $isFirstColumn={index === 0}
                    $isSpecific={crosstableResult.rows[rowIndex].id <= -9998}
                    $isTotal={index === 0}
                    align='center'
                    sx={index === 0 ? { position: 'sticky', left: totalColumnPosition } : {}}
                    variant="head"
                >
                    {renderCellContent(row[index])}
                </TableCellBodyStyled>
            ))}
        </>
    );

    return (
        <TableVirtuoso
            components={VirtuosoTableComponents}
            data={dataRows}
            fixedHeaderContent={fixedHeaderContent}
            // Every row is drawn from the first render, the measured height is then exact.
            initialItemCount={dataRows.length}
            itemContent={rowContent}
            style={{
                height: (contentHeight ?? (ESTIMATED_HEADER_HEIGHT + (ESTIMATED_ROW_HEIGHT * dataRows.length))) + HORIZONTAL_SCROLLBAR_HEIGHT,
                willChange: 'transform',
                boxShadow: 'none',
            }}
            totalListHeightChanged={setContentHeight}
        />
    );
};

export default CrosstableIndicator;
